import type { GradingCompany, PortfolioEntry } from "../types";

// PortfolioEntry (camelCase) <-> a `portfolio_entries` row (snake_case), specs/11 Data and
// interfaces. `user_id` is the store's business, and scan photos are never saved (FR-67).

export interface PortfolioRow {
  id: string;
  card_id: string;
  provider: PortfolioEntry["provider"];
  language: PortfolioEntry["language"];
  card_snapshot: PortfolioEntry["cardSnapshot"];
  variant: string;
  condition: PortfolioEntry["condition"];
  grading_company: GradingCompany | null;
  grade: number | null;
  quantity: number;
  purchase_price: number | null;
  purchase_date: string | null;
  manual_value: number | null;
  notes: string | null;
  added_at: string;
  updated_at: string;
}

export function entryToRow(entry: PortfolioEntry): PortfolioRow {
  return {
    id: entry.id,
    card_id: entry.cardId,
    provider: entry.provider,
    language: entry.language,
    card_snapshot: entry.cardSnapshot,
    variant: entry.variant,
    condition: entry.condition,
    grading_company: entry.grading?.company ?? null,
    grade: entry.grading?.grade ?? null,
    quantity: entry.quantity,
    purchase_price: entry.purchasePrice ?? null,
    purchase_date: entry.purchaseDate || null,
    manual_value: entry.manualValue ?? null,
    notes: entry.notes || null,
    added_at: entry.addedAt,
    updated_at: entry.updatedAt,
  };
}

/** Postgres numerics may come back as strings, and timestamps as "…+00:00". */
const amount = (value: number | string | null) => (value === null ? undefined : Number(value));
const iso = (timestamp: string) => new Date(timestamp).toISOString();

export function rowToEntry(row: PortfolioRow): PortfolioEntry {
  const entry: PortfolioEntry = {
    id: row.id,
    cardId: row.card_id,
    provider: row.provider,
    language: row.language,
    cardSnapshot: row.card_snapshot,
    variant: row.variant,
    condition: row.condition,
    quantity: row.quantity,
    addedAt: iso(row.added_at),
    updatedAt: iso(row.updated_at),
  };
  // Optional fields are left out rather than set to undefined, like entries made in the app.
  const purchasePrice = amount(row.purchase_price);
  if (purchasePrice !== undefined) entry.purchasePrice = purchasePrice;
  if (row.purchase_date) entry.purchaseDate = row.purchase_date;
  const manualValue = amount(row.manual_value);
  if (manualValue !== undefined) entry.manualValue = manualValue;
  if (row.notes) entry.notes = row.notes;
  if (row.grading_company !== null && row.grade !== null) {
    entry.grading = { company: row.grading_company, grade: Number(row.grade) };
  }
  return entry;
}
